import { SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { AttendantsDataTable } from "@/components/attendants/attendants.data-table";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { attendantsApi } from "@/services/attendants/attendants.api";
import { $fetch } from "@/lib/fetch";
import {
  type AttendantDto,
  type AttendantFormDto,
  attendantsDtoSchema,
} from "slde-api/dto-schemas";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AttendantForm } from "@/components/attendants/attendant.form";

export default function AttendantsPage() {
  const queryClient = useQueryClient();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [attendantToUpdate, setAttendantToUpdate] =
    useState<AttendantDto | null>(null);

  const {
    data: { items },
  } = useSuspenseQuery({
    queryKey: attendantsApi.attendants.queryKey(),
    queryFn: async () =>
      await $fetch(attendantsApi.attendants.route(), {
        output: attendantsDtoSchema,
        throw: true,
      }),
  });

  const { mutate: createAttendant, isPending: isCreatingAttendant } =
    useMutation({
      mutationFn: async (form: AttendantFormDto) =>
        await $fetch(attendantsApi.attendants.route(), {
          method: "POST",
          body: form,
        }),
      onSuccess: () => {
        toast.success("Personel utworzono pomyślnie");
        setCreateDialogOpen(false);
        void queryClient.invalidateQueries();
      },
      onError: (e) => {
        if (e instanceof Error) {
          toast.error(`Wystąpił błąd przy dodawaniu personelu: ${e.message}`);
        }
        console.error(e);
      },
    });

  const { mutate: updateAttendant, isPending: isUpdatingAttendant } =
    useMutation({
      mutationFn: async ({ id, ...form }: AttendantFormDto & { id: number }) =>
        await $fetch(attendantsApi.attendant.route(id), {
          method: "PATCH",
          body: form,
        }),
      onSuccess: () => {
        toast.success("Zaktualizowano personel");
        setAttendantToUpdate(null);
        void queryClient.invalidateQueries();
      },
      onError: (e) => {
        if (e instanceof Error) {
          toast.error(`Wystąpił błąd przy aktualizacji personel: ${e.message}`);
        }
        console.error(e);
      },
    });

  const { mutate: deleteAttendant, isPending: isDeletingAttendant } =
    useMutation({
      mutationFn: async ({ id }: { id: number }) =>
        await $fetch(attendantsApi.attendant.route(id), {
          method: "DELETE",
        }),
      onSuccess: () => {
        toast.success("personel został usunięty");
        void queryClient.invalidateQueries();
      },
      onError: (e) => {
        if (e instanceof Error) {
          toast.error(`Wystąpił błąd przy usuwaniu personelu: ${e.message}`);
        }
        console.error(e);
      },
    });

  return (
    <div>
      <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
        <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator
            orientation="vertical"
            className="mx-2 data-[orientation=vertical]:h-4"
          />
          <h1 className="text-base font-medium">Zarządzanie personelem</h1>
          <div className="ml-auto flex items-center gap-2">
            <Button onClick={() => setCreateDialogOpen(true)}>
              Dodaj personel
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
        <AttendantsDataTable
          data={items}
          actions={{
            onUpdateClick: (attendant) => setAttendantToUpdate(attendant),
            onDeleteClick: (id) => deleteAttendant({ id }),
          }}
        />
      </div>

      <Dialog
        open={createDialogOpen || !!attendantToUpdate}
        onOpenChange={(open) => {
          setCreateDialogOpen(open);
          setAttendantToUpdate(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {!!attendantToUpdate ? "Edytuj personel" : "Dodaj personel"}
            </DialogTitle>
          </DialogHeader>

          <AttendantForm
            onSubmit={(form) =>
              !!attendantToUpdate
                ? updateAttendant({ id: attendantToUpdate.id, ...form })
                : createAttendant(form)
            }
            defaultValues={
              attendantToUpdate
                ? {
                    fullName: attendantToUpdate.fullName,
                    pin: attendantToUpdate.pin,
                  }
                : undefined
            }
            disabled={
              isCreatingAttendant || isUpdatingAttendant || isDeletingAttendant
            }
            loading={
              isCreatingAttendant || isUpdatingAttendant || isDeletingAttendant
            }
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
